import React from "react"
import { useTranslation } from "react-i18next"
import styles from "./SettingsGroupContent.module.scss"
import { Button } from "react-bootstrap"
import { testId as rolesTestIds } from "../../utility/tests/testId/roles"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faSquare, faTrash } from "@fortawesome/pro-light-svg-icons"
import { formTranslation } from "../../locales/form"
import CheckBoxValidatableInput from "../CheckBoxValidatableInput/CheckBoxValidatableInput"
import { Checkable, Settings } from "../../types/settings"
import AddButton from "../AddButton/AddButton"
import cn from "classnames"
import usePermissionHelpers from "../../hooks/usePermissionHelpers"
import { useFormikContext } from "formik"
import { getLanguage } from "../../utility/common/language"
import i18n from "i18next"

const tNamespace = "roles:"

export interface SettingsGroupContentProps<T> {
    settingsValues: Settings<T>
    getLabel: (value: T) => string
    disabled?: boolean
    name: string
    isInRole?: boolean
    permHelpers?: ReturnType<typeof usePermissionHelpers>
    openSidebar?: () => void
    isUseSort?: boolean
    testId?: string
}

type SettingsGroup<T> = {
    [key: string]: {
        value: T
        isChecked: boolean
    }
}

interface FormValues {
    [groupName: string]: SettingsGroup<string>
}

function SettingsGroupContent<T>(props: SettingsGroupContentProps<T>) {
    const {
        settingsValues,
        getLabel,
        isInRole = false,
        disabled,
        name,
        permHelpers,
        openSidebar,
        isUseSort,
        testId
    } = props

    const locale = getLanguage(i18n)

    const sortValues = (values: [string, Checkable<T>][]) =>
        values.sort((a, b) => {
            const valA = getLabel(a[1].value).toLowerCase()
            const valB = getLabel(b[1].value).toLowerCase()

            return valA.localeCompare(valB, locale)
        })

    const { t } = useTranslation()

    const { setValues, setFieldValue } = useFormikContext()

    const keys = Object.keys(settingsValues)
    const isEmptySettingsGroup = keys.length === 0

    const handleToggleAll = (enable: boolean) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.stopPropagation()

        const regexSettingArray = /\[\d+]./g
        const settingIsArray = regexSettingArray.test(name)
        if (settingIsArray) {
            keys.forEach(key => setFieldValue(`${name}.${key}.isChecked`, enable))
            return
        }

        const updates: Settings<string> = keys.reduce(
            (acc, key) => ({
                ...acc,
                [key]: {
                    value: settingsValues[key].value,
                    isChecked: enable
                }
            }),
            {}
        )
        setValues((prev: FormValues) => ({ ...prev, [name]: updates }))
    }

    const values = isUseSort ? sortValues(Object.entries(settingsValues)) : Object.entries(settingsValues)

    return (
        <>
            {!isEmptySettingsGroup && (
                <div className={styles.settingsGroupContent__buttons}>
                    <Button
                        variant="light"
                        onClick={handleToggleAll(true)}
                        className={styles.settingsGroupContent__btn}
                        data-testid={testId ? `${testId}-enable-all-button` : rolesTestIds.enableAll}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faCheck} className={styles.settingsGroupContent__btnIcon} />
                        {t(formTranslation.enableAll)}
                    </Button>
                    <Button
                        variant="light"
                        onClick={handleToggleAll(false)}
                        className={styles.settingsGroupContent__btn}
                        data-testid={testId ? `${testId}-disable-all-button` : rolesTestIds.turnOffAll}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faSquare} className={styles.settingsGroupContent__btnIcon} />
                        {t(formTranslation.turnOffAll)}
                    </Button>
                    {isInRole && (
                        <Button
                            variant="light"
                            onClick={permHelpers?.handleDeleteAll}
                            className={styles.settingsGroupContent__btn}
                            data-testid={rolesTestIds.deleteAllRights}
                            disabled={disabled}
                        >
                            <FontAwesomeIcon icon={faTrash} className={styles.settingsGroupContent__btnIcon} />
                            {t(`${tNamespace}delete-all-permissions`)}
                        </Button>
                    )}
                </div>
            )}
            <div
                className={cn(
                    styles.settingsGroupContent__inputsContainer,
                    isEmptySettingsGroup && styles.settingsGroupContent__inputsContainer_empty
                )}
            >
                {isInRole ? (
                    <>
                        {values.map(([k, v]) => (
                            <div className={styles.settingsGroupContent__checkBoxInput} key={k}>
                                <CheckBoxValidatableInput
                                    id={"form" + k}
                                    disabled={disabled}
                                    name={`${name}.${k}.isChecked`}
                                    label={getLabel(v.value)}
                                />
                                <FontAwesomeIcon
                                    icon={faTrash}
                                    onClick={() => permHelpers?.handleDeletePermission(k)}
                                    className={styles.settingsGroupContent__deletePermissionBtn}
                                />
                            </div>
                        ))}
                        {openSidebar && (
                            <AddButton
                                variant="primary"
                                className={styles.settingsGroupContent__addPermissionBtn}
                                text={t(`${tNamespace}add-permission`)}
                                onClick={openSidebar}
                                testId={rolesTestIds.addPermission}
                            />
                        )}
                    </>
                ) : (
                    <>
                        {values.map(([k, v]) => (
                            <div className={styles.settingsGroupContent__checkBoxInput} key={k}>
                                {/* className disabled вместо соответствующего пропа используется, потому что
                                    при использовании пропа и активации чекбоксов на инстансе отображение чекбоксов
                                    не происходит должным образом: https://youtrack.craft-talk.ru/issue/alfa-441#focus=Comments-4-75033.0-0
                                 */}
                                <CheckBoxValidatableInput
                                    id={"form" + k}
                                    className={disabled ? styles.settingsGroupContent__checkBox_disabled : undefined}
                                    name={`${name}.${k}.isChecked`}
                                    label={getLabel(v.value)}
                                    data-testid={`${testId}-catalog-checkbox`}
                                />
                            </div>
                        ))}
                    </>
                )}
            </div>
        </>
    )
}

export default SettingsGroupContent
